/**
 * Shared internal helpers for the schema-management interface modules.
 *
 * `getSummaryPreview` computes the summary-preview / successFailed report
 * shared by reset / update-defaults / import flows.
 *
 * `modifySchema` and `modifyConfig` mutate the client schema/config to
 * accommodate newly-added clients (used by addNewClient, editScheduledDraft's
 * version loader, and import commit).
 */

import cloneDeep from "lodash/cloneDeep";
import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import isEqual from "lodash/isEqual";
import { getStore } from "@store";
import { isObject } from "@utils/input-helpers";
import { isEmptyObject } from "@utils/object-helpers";
import { updateConfig, updateSchema } from "@features/schema-management/actions";

// getSummaryPreview: computes {newConfig, summaryPreview, successFailedImportedKeysReport}.
// isArray: whether the schema/config is array-typed (SYF card access-style).
export const getSummaryPreview = async (
  isArray,
  initialConfig,
  stringConfigSection,
  updatedConfig,
  configToCopy,
  contentSection = null,
  store = getStore()
) => {
  const { isAthosConfig } = store.getState().schemaAllView;
  let summaryPreview = {};
  let successFailedImportedKeysReport = {};
  let importNewClient =
    isAthosConfig &&
    configToCopy &&
    configToCopy !== "entire" &&
    !initialConfig.hasOwnProperty(configToCopy);
  let smConfigToCopy =
    configToCopy === "entire" || importNewClient
      ? cloneDeep(initialConfig)
      : configToCopy
        ? cloneDeep(initialConfig[configToCopy])
        : cloneDeep(updatedConfig);
  smConfigToCopy = contentSection
    ? cloneDeep(smConfigToCopy[contentSection])
    : smConfigToCopy;
  let newConfig =
    configToCopy === "entire" || importNewClient
      ? cloneDeep(initialConfig)
      : configToCopy
        ? cloneDeep(initialConfig[configToCopy])
        : "";

  stringConfigSection.forEach((fullPath) => {
    let newValue = get(
      updatedConfig ? updatedConfig : stringConfigSection,
      fullPath
    );
    let strPath =
      configToCopy && configToCopy !== "entire" && !importNewClient
        ? fullPath.substring(fullPath.indexOf(".") + 1)
        : fullPath;

    if (has(smConfigToCopy, strPath)) {
      const oldValue = get(
        configToCopy ? smConfigToCopy : initialConfig,
        strPath
      );
      if (
        oldValue === null ||
        oldValue === undefined ||
        newValue === null ||
        (typeof newValue === typeof oldValue &&
          isObject(newValue) === isObject(oldValue))
      ) {
        if (!isEqual(newValue, oldValue)) {
          summaryPreview[fullPath] = newValue;
        }
        successFailedImportedKeysReport[fullPath] = { disable: false };
        if (contentSection) {
          set(newConfig, `${contentSection}.${strPath}`, newValue);
        } else if (configToCopy) {
          set(newConfig, strPath, newValue);
        }
      }
    } else {
      summaryPreview[fullPath] = newValue;
      successFailedImportedKeysReport[fullPath] = {
        disable: isArray || isAthosConfig ? false : true,
      };
    }
  });

  return { newConfig, summaryPreview, successFailedImportedKeysReport };
};

export const modifySchema = async (clientName, store = getStore()) => {
  const { defaultModifiedSchema } = store.getState().schema;
  let newSchema = {};
  if (!isEmptyObject(defaultModifiedSchema.properties)) {
    newSchema = {
      [clientName]: {
        ...Object.values(defaultModifiedSchema.properties)[0],
        title: clientName,
      },
    };
  }
  store.dispatch(updateSchema(newSchema));
};

export const modifyConfig = (clientName, store = getStore()) => {
  const { defaultConfig } = store.getState().schemaAllView;
  let newConfig = {};
  if (!isEmptyObject(defaultConfig)) {
    newConfig = { [clientName]: Object.values(defaultConfig)[0] };
  }
  store.dispatch(updateConfig(newConfig));
  return newConfig;
};
